/**
 * Lean keyboard-shortcut hook.
 *
 * Skips firing when focus is in an editable element (input/textarea/contenteditable).
 * Supports single-key bindings with optional modifier check.
 *
 * Usage:
 *   useHotkeys({
 *     j: () => moveDown(),
 *     k: () => moveUp(),
 *     e: () => markDone(),
 *     '?': () => setShowHelp(v => !v),
 *   });
 */
import { useEffect } from 'react';

export type HotkeyMap = Record<string, (e: KeyboardEvent) => void>;

function isEditableTarget(t: EventTarget | null): boolean {
  if (!(t instanceof HTMLElement)) return false;
  if (t.isContentEditable) return true;
  const tag = t.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  return false;
}

export function useHotkeys(map: HotkeyMap, opts: { enabled?: boolean } = {}) {
  const enabled = opts.enabled ?? true;
  useEffect(() => {
    if (!enabled) return;
    function onKey(e: KeyboardEvent) {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (isEditableTarget(e.target)) return;
      const handler = map[e.key];
      if (handler) {
        handler(e);
        e.preventDefault();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [map, enabled]);
}
